<template>
  <div class="work-detail-page" :class="{ 'is-preview': isPreview }">
    <div v-show="!isImmersive" ref="pageContent" class="work-detail-content" :class="['layout-' + viewerLayout]"
      tabindex="-1">
      <!-- Work not found -->
      <div v-if="showNotAvailable" class="not-available-container">
        <div class="not-available-icon">
          <CrossIcon />
        </div>
        <h2 class="not-available-title">
          Artwork Not Found
        </h2>
        <p class="not-available-message">
          <span v-if="requestedSlug">The artwork "{{ requestedSlug }}" could not be found.</span>
          <span v-else>This artwork is not available for viewing.</span>
        </p>
        <p class="not-available-redirect">
          Redirecting to gallery...
        </p>
      </div>

      <!-- Archived work accessed via direct URL (no gallery context) -->
      <div v-else-if="isArchivedWithoutContext" class="not-available-container archived-container">
        <div class="not-available-icon archived-icon">
          📦
        </div>
        <h2 class="not-available-title">
          Archived Work
        </h2>
        <p class="not-available-message">
          This work has been archived and is not available for public viewing.
        </p>
        <p class="not-available-redirect">
          Redirecting to gallery...
        </p>
      </div>

      <!-- Loading state while work data is being fetched -->
      <div v-if="!work && !showNotAvailable && !isArchivedWithoutContext" class="modal-loading-container">
        <div class="loading-spinner" />
      </div>

      <!-- Ribbon: only shown in focus mode (hidden in always-open on desktop via CSS) -->
      <div v-if="work && !isArchivedWithoutContext" class="modal-ribbon" :style="panelStyle">
        <div class="ribbon-text">
          <p class="ribbon-line">
            <span class="ribbon-title">{{ work.name }}</span>
          </p>
        </div>
        <div class="ribbon-controls">
          <button class="modal-control-button modal-slideshow-button"
            :aria-label="isSlideshowPlaying ? 'Pause slideshow' : 'Start slideshow'"
            :title="isSlideshowPlaying ? 'Pause slideshow' : 'Start slideshow'" @click="toggleSlideshow">
            <PlayIcon v-if="!isSlideshowPlaying" />
            <PauseIcon v-else />
          </button>
          <button class="modal-control-button modal-random-button" aria-label="Random artwork" title="Random artwork"
            @click="goToRandom">
            <RandomIcon />
          </button>
          <button v-if="viewerLayout === 'focus'" class="panel-toggle-pill" :class="{ open: isPanelOpen || showForm }"
            :aria-label="isPanelOpen || showForm ? 'Hide info panel' : 'Show info panel'"
            :title="isPanelOpen || showForm ? 'Hide info' : 'Show info'" @click="toggleInfoPanel">
            {{ isPanelOpen || showForm ? '‹ Info' : 'Info ›' }}
          </button>
          <button class="modal-control-button modal-close-button ribbon-close" aria-label="Close modal" title="Close"
            @click="onCancel">
            <CloseIcon />
          </button>
        </div>
      </div>

      <!-- Body: image + info panel -->
      <div v-if="!showNotAvailable && !isArchivedWithoutContext" class="modal-body">
        <div v-if="work && !isArchivedWithoutContext" ref="viewerImage" class="viewer-image"
          :class="imageContainerClasses" :style="viewerImageStyle" @touchstart="onTouchStart" @touchend="onTouchEnd"
          @click="onImageClick">
          <img :src="mainImageSrc" :alt="altText" loading="eager" decoding="async" fetchpriority="high" @load="onImageLoad"
            @error="onMainImageError">
          <button v-if="hasPrevious" class="modal-control-button nav-button nav-prev" aria-label="Previous artwork"
            title="Previous" @click.stop="goToPrevious">
            <PreviousIcon />
          </button>
          <button v-if="hasNext" class="modal-control-button nav-button nav-next" aria-label="Next artwork"
            title="Next" @click.stop="goToNext">
            <NextIcon />
          </button>
        </div>
        <div v-if="work && !isArchivedWithoutContext" class="side-panel"
          :class="{ 'panel-is-open': isPanelOpen || isAlwaysOpen }" :style="panelStyle">
          <div class="info-panel-body" :class="{ 'form-open': showForm }">
            <!-- Inline controls for always-open mode (hidden in focus via CSS) -->
            <div v-if="isAlwaysOpen" class="panel-controls">
              <button class="modal-control-button modal-slideshow-button"
                :aria-label="isSlideshowPlaying ? 'Pause slideshow' : 'Start slideshow'"
                :title="isSlideshowPlaying ? 'Pause slideshow' : 'Start slideshow'" @click="toggleSlideshow">
                <PlayIcon v-if="!isSlideshowPlaying" />
                <PauseIcon v-else />
              </button>
              <button class="modal-control-button modal-random-button" aria-label="Random artwork"
                title="Random artwork" @click="goToRandom">
                <RandomIcon />
              </button>
              <button class="modal-control-button modal-close-button" aria-label="Close modal" title="Close"
                @click="onCancel">
                <CloseIcon />
              </button>
            </div>
            <div class="title-nav">
              <button v-if="hasPrevious" class="modal-control-button title-nav-btn" aria-label="Previous artwork"
                title="Previous artwork" @click="goToPrevious">
                <PreviousIcon />
              </button>
              <span v-else class="title-nav-spacer" />
              <h1 v-if="work.name" class="work-title">{{ work.name }}</h1>
              <h1 v-else-if="isPreview" class="work-title preview-placeholder">Artwork Title</h1>
              <button v-if="hasNext" class="modal-control-button title-nav-btn" aria-label="Next artwork"
                title="Next artwork" @click="goToNext">
                <NextIcon />
              </button>
              <span v-else class="title-nav-spacer" />
            </div>
            <p v-if="showCaption && galleryCaption" class="gallery-caption">
              {{ galleryCaption }}
            </p>
            <p v-else-if="showCaption && isPreview" class="gallery-caption preview-placeholder">
Subtitle will appear
              here.
</p>
            <h2 v-if="showArtist && config.artist.name">
              by {{ config.artist.name }}
            </h2>

            <div v-if="showDescription && (work.description || isPreview)" class="work-description"
              :class="{ 'short-description': isShortDescription }">
              <ReadMore v-if="work.description" :text="work.description" :threshold="350" />
              <span v-else-if="isPreview" class="preview-placeholder">Artwork description will appear here.</span>
            </div>
            <div v-if="!hasContentAboveDetails" class="details-spacer" />
            <ul v-if="hasVisibleDetails" class="work-details">
              <li v-if="showYear && (work.year || isPreview)">
                <template v-if="work.year">{{ work.year }}</template>
                <span v-else-if="isPreview" class="preview-placeholder">Year</span>
              </li>
              <li v-if="showFramed || (showSize && (work.size?.width && work.size?.height || isPreview))">
                <template v-if="showSize && work.size?.width && work.size?.height">{{ sizeText }}</template>
                <span v-else-if="showSize && isPreview" class="preview-placeholder">W × H</span>
                <span v-if="showFramed && showSize && ((work.size?.width && work.size?.height) || isPreview)"> · </span>
                <template v-if="showFramed">{{ work.isFramed ? 'Framed' : 'Not Framed' }}</template>
              </li>
              <li v-if="showSold && work.isSold">Sold</li>
              <li v-else-if="showSold && isPreview" class="preview-placeholder">Sold</li>
              <li v-if="showPrice && (isPriceVisible || isPreview)">
                <template v-if="isPriceVisible">{{ formatPrice(work.price.value) }} {{ work.price.currency }}</template>
                <span v-else class="preview-placeholder">Price</span>
              </li>
              <li v-if="showPhone && artistPhone">Phone {{ artistPhone }}</li>
              <li v-else-if="showPhone && isPreview" class="preview-placeholder">Phone number will appear here.</li>
            </ul>
            <div v-if="showTags && (workTagGroups.length || isPreview)" class="work-tags-section">
              <span class="work-tags-heading">Tags</span>
              <div v-if="workTagGroups.length" class="work-tags">
                <span v-for="group in workTagGroups" :key="group.groupId" class="work-tag-group">
                  <span v-for="tag in group.tags" :key="tag.id" class="work-tag-chip">{{ tag.label }}</span>
                </span>
              </div>
              <span v-else class="preview-placeholder">Tags</span>
            </div>
            <div v-if="!work.isSold" id="cta">
              <buttonCustom class="btnGroup" :button="buttonType" :text="contactButtonText" @click="showForm = true">
                <template #icon>
                  <ContactIcon />
                </template>
              </buttonCustom>
            </div>
          </div>
          <WorkContact v-if="showForm" :work="work" @go-back="showForm = false" />
        </div>
      </div>
    </div>

    <!-- Immersive view -->
    <Transition name="immersive-fade">
      <div v-if="isImmersive && work" ref="immersiveOverlay" class="immersive-overlay" :style="imageBgStyle"
        @touchstart="onTouchStart" @touchmove.prevent="onImmersivePan" @touchend.prevent="onTouchEnd">
        <img :src="mainImageSrc" class="immersive-img" :alt="altText" :style="immersiveImgStyle">
        <div class="immersive-title" :class="{ visible: showImmersiveTitle }">{{ work.name }}</div>
        <button v-if="hasPrevious" class="modal-control-button immersive-nav immersive-prev"
          aria-label="Previous artwork" title="Previous" @touchend.stop.prevent="goToPrevious"
          @click.stop="goToPrevious">
          <PreviousIcon />
        </button>
        <button v-if="hasNext" class="modal-control-button immersive-nav immersive-next" aria-label="Next artwork"
          title="Next" @touchend.stop.prevent="goToNext" @click.stop="goToNext">
          <NextIcon />
        </button>
        <button class="modal-control-button immersive-back" aria-label="Back to detail view" title="Back"
          @touchend.stop.prevent="exitImmersive" @click.stop="exitImmersive">
          <BackIcon />
        </button>
      </div>
    </Transition>
  </div>
</template>

<script>
import WorkContact from '@/components/WorkContact.vue'
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import { store, mutations } from '@/store'
import { mark, summary, markClickToModal, endClickToModal } from '@/utils/perfMarks'
import { directGcsUrl, wrapperToDirectUrl } from '@/utils/imageUrl'
import { artistConfig as defaultConfig } from '@/config/artist.config'
import { resolveViewerColor, panelOutlineVars } from '@/utils/viewerColors'
import { normalizeTagGroups, resolveWorkTags, artworkAlt } from '@/utils/tags'
import { formatSize, normalizeUnit } from '@/utils/sizeUnits'
import RandomIcon from '@/assets/icons/random.vue'
import PlayIcon from '@/assets/icons/play.vue'
import PauseIcon from '@/assets/icons/pause.vue'
import CloseIcon from '@/assets/icons/close.vue'
import BackIcon from '@/assets/icons/back.vue'
import PreviousIcon from '@/assets/icons/previous.vue'
import NextIcon from '@/assets/icons/next.vue'
import CrossIcon from '@/assets/icons/cross.vue'
import ContactIcon from '@/assets/icons/block-contact.vue'
import ReadMore from '@/components/ReadMore.vue'

export default {
  name: 'WorkModal',
  components: {
    WorkContact,
    ContactIcon,
    RandomIcon,
    PlayIcon,
    PauseIcon,
    CloseIcon,
    BackIcon,
    PreviousIcon,
    NextIcon,
    CrossIcon,
    ReadMore
  },
  props: {
    // Admin preview: shows previewWork without the route, page title or keyboard handling
    preview: { type: Boolean, default: false },
    previewWork: { type: Object, default: null },
    previewWorks: { type: Array, default: () => [] },
    panelOpen: { type: Boolean, default: false }
  },
  setup(props) {
    // Create a reactive computed reference to the artist config
    const config = computed(() => store.artistConfig || defaultConfig)

    if (props.preview) return { headObj: null, config }

    // We'll use a reactive head object that can be updated from the component
    const headObj = useHead({
      title: 'Loading...',
      meta: [
        { name: 'description', content: '' }
      ]
    })

    return { headObj, config }
  },
  data() {
    return {
      showForm: false,
      imageLoaded: false,
      showNotAvailable: false,
      requestedSlug: null,
      redirectTimeout: null,
      isPanelOpen: this.panelOpen,
      isSlideshowPlaying: false,
      slideshowInterval: null,
      slideshowDelay: 3000, // 3 seconds between slides
      touchStartX: null,
      touchStartY: null,
      isImmersive: false,
      isMultiTouch: false,
      showImmersiveTitle: false,
      immersiveTitleTimeout: null,
      lockedImageHeight: null,
      immersiveScale: 1,
      immersiveTranslateX: 0,
      immersiveTranslateY: 0,
      immersivePanning: false,
      pinchStartDist: null,
      pinchMidX: 0,
      pinchMidY: 0,
      lastTapTime: 0,
      imageDirectFailed: false // direct GCS full-image 403'd - fall back to the wrapper URL
    }
  },
  computed: {
    // Prefer the fast direct GCS full image; fall back to the wrapper URL on error.
    mainImageSrc() {
      if (this.imageDirectFailed) return this.work?.imageFile || ''
      const direct = this.work?.imageRef
        ? directGcsUrl(this.work.imageRef)
        : wrapperToDirectUrl(this.work?.imageFile)
      return direct || this.work?.imageFile || ''
    },
    viewerLayout() {
      const layout = this.config.gallery?.viewerLayout || 'always-open'
      // Migrate legacy values
      if (layout === 'split') return 'always-open'
      if (layout === 'panel' || layout === 'drawer') return 'focus'
      return layout
    },
    isAlwaysOpen() {
      return this.viewerLayout === 'always-open'
    },
    // Navigation context from store (set by gallery blocks)
    navContext() {
      return store.navigationContext
    },
    // Navigation works - use context if available (e.g., from gallery block), otherwise global
    navigationWorks() {
      if (this.preview) return this.previewWorks
      return this.navContext?.works || store.works || []
    },
    // Source path to return to when closing (if from gallery block)
    sourcePath() {
      return this.navContext?.sourcePath || null
    },
    // Page slug for route context (from navigation context or route params)
    pageSlug() {
      return this.navContext?.pageSlug ?? this.$route.params.pageSlug ?? null
    },
    work() {
      if (this.preview) return this.previewWork
      const slug = this.$route.params.workSlug
      return store.allWorks.find(el => el.slug === slug || el.id === slug)
    },
    // Viewer visibility settings
    showArtist() {
      return this.config.gallery?.viewerShowArtist !== false
    },
    showPhone() {
      return store.artistConfig?.gallery?.viewerShowPhone === true
    },
    artistPhone() {
      return store.artistConfig?.artist?.phone || ''
    },
    showCaption() {
      return this.config.gallery?.viewerShowCaption !== false
    },
    showDescription() {
      return this.config.gallery?.viewerShowDescription !== false
    },
    isShortDescription() {
      return this.work?.description && this.work.description.length < 80
    },
    showYear() {
      return this.config.gallery?.viewerShowYear !== false
    },
    showTags() {
      return this.config.gallery?.viewerShowTags !== false
    },
    // The work's tags, resolved from stored ids to labels and grouped/ordered
    // by config. Only groups/tags still present in config appear.
    workTagGroups() {
      const groups = normalizeTagGroups(this.config.artist?.tagGroups)
      return resolveWorkTags(this.work, groups)
    },
    altText() {
      return artworkAlt(this.work, normalizeTagGroups(this.config.artist?.tagGroups)) || 'Artwork'
    },
    showSize() {
      return this.config.gallery?.viewerShowSize !== false
    },
    sizeText() {
      return formatSize(this.work?.size, normalizeUnit(this.config.gallery?.unit))
    },
    showFramed() {
      return this.config.gallery?.viewerShowFramed !== false
    },
    showPrice() {
      return this.config.gallery?.viewerShowPrice !== false
    },
    showSold() {
      return this.config.gallery?.viewerShowSold !== false
    },
    showPriceWhenSold() {
      return this.config.gallery?.viewerShowPriceWhenSold === true
    },
    isPriceVisible() {
      const w = this.work
      return !!((!w?.isSold || this.showPriceWhenSold) && w?.price?.isPriceShown && w?.price?.value)
    },
    hasVisibleDetails() {
      // Check if any detail list item would be visible
      return this.showYear || (this.showTags && this.workTagGroups.length) || this.showSize || this.showFramed || this.showPrice || this.showSold || this.showPhone
    },
    hasContentAboveDetails() {
      return (this.showArtist && this.config.artist.name) ||
        (this.showCaption && this.galleryCaption) ||
        (this.showDescription && (this.work?.description || this.isPreview))
    },
    contactButtonText() {
      return this.config.gallery?.contactButtonText || 'Contact'
    },
    // Caption: use gallery block caption if set, otherwise fall back to work's own caption
    galleryCaption() {
      if (this.navContext?.works && this.work) {
        const match = this.navContext.works.find(
          p => p.slug === this.work.slug || p.id === this.work.id
        )
        if (match?.caption) return match.caption
      }
      return this.work?.caption || ''
    },
    isPreview() {
      return this.preview
    },
    // Navigation computed properties (uses navigationWorks for scoped navigation)
    currentIndex() {
      if (!this.work || !this.navigationWorks) return -1
      return this.navigationWorks.findIndex(
        p => p.slug === this.work.slug || p.id === this.work.id
      )
    },
    hasPrevious() {
      return this.currentIndex > 0
    },
    hasNext() {
      return this.navigationWorks && this.currentIndex < this.navigationWorks.length - 1
    },
    viewerBackground() {
      return resolveViewerColor(this.config.gallery?.viewerBackground, this.config.theme)
    },
    imageContainerClasses() {
      return {
        loading: !this.imageLoaded,
        loaded: this.imageLoaded
      }
    },
    imageBgStyle() {
      if (this.viewerBackground) {
        return { backgroundColor: this.viewerBackground }
      }
      return {}
    },
    viewerImageStyle() {
      const style = { ...this.imageBgStyle }
      if (this.lockedImageHeight) style.minHeight = this.lockedImageHeight
      return style
    },
    // Check if work is archived and accessed without gallery context (direct URL)
    isArchivedWithoutContext() {
      // If work doesn't exist, this isn't an "archived" case - it's "not found"
      if (!this.work || this.preview) return false
      // If work is published (shown), it's accessible everywhere
      if (this.work.isWorkShown === true) return false
      // If there's navigation context (came from a gallery block), allow access
      if (this.navContext) return false
      // Archived work accessed via direct URL - block access
      return true
    },
    // Viewer appearance settings
    viewerPanelBg() {
      return resolveViewerColor(this.config.gallery?.viewerPanelBg, this.config.theme)
    },
    viewerPanelText() {
      return resolveViewerColor(this.config.gallery?.viewerPanelText, this.config.theme)
    },
    viewerButtonStyle() {
      return this.config.gallery?.viewerButtonStyle || 'primary'
    },
    panelStyle() {
      const style = {}
      if (this.viewerPanelBg) style['--panel-bg'] = this.viewerPanelBg
      if (this.viewerPanelText) {
        style.color = this.viewerPanelText
        style['--panel-text'] = this.viewerPanelText
      }
      if (this.viewerPanelBg || this.viewerPanelText) {
        const theme = this.config.theme || {}
        const bg = this.viewerPanelBg || theme.bgColor
        const text = this.viewerPanelText || theme.textColor
        if (bg && text) Object.assign(style, panelOutlineVars(bg, text))
      }
      return style
    },
    buttonType() {
      // Style names match the visitor button types in buttons.css
      return ['primary', 'secondary', 'outline'].includes(this.viewerButtonStyle) ? this.viewerButtonStyle : 'primary'
    },
    immersiveImgStyle() {
      const transition = this.immersivePanning ? 'none' : 'transform 0.3s ease'
      if (this.immersiveScale === 1) return { transition }
      return {
        transform: `translate(${this.immersiveTranslateX}px, ${this.immersiveTranslateY}px) scale(${this.immersiveScale})`,
        transformOrigin: '0 0',
        transition
      }
    }
  },
  watch: {
    panelOpen(open) {
      this.isPanelOpen = open
    },
    viewerLayout() {
      if (this.preview) this.isPanelOpen = this.panelOpen
    },
    // Watch work changes to update SEO meta tags
    work: {
      handler(newWork) {
        if (newWork && this.preview) {
          this.imageDirectFailed = false
        } else if (newWork) {
          this.imageDirectFailed = false // reset fallback for the new work
          mark('modal:work:ready')
          markClickToModal('work:ready (route resolved + work found in store)')
          const workName = newWork.name || 'Work'
          const artistName = this.config.artist.name
          const artMovement = this.config.artist.movement
          const description = newWork.description ||
            `${workName} by ${artistName}. Original ${artMovement ? artMovement + ' ' : ''}artwork.`
          // Flatten the work's tags into a space-separated string for the meta description.
          // Resolve tag ids to labels for the meta description.
          const tagGroups = normalizeTagGroups(this.config.artist?.tagGroups)
          const tagsText = resolveWorkTags(newWork, tagGroups)
            .flatMap(g => g.tags.map(t => t.label)).join(' ')

          // Update the head tags
          this.headObj.patch({
            title: `${workName} by ${artistName}${artMovement ? ' - ' + artMovement : ''}`,
            meta: [
              { name: 'description', content: `${description} ${tagsText}` }
            ]
          })
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.preview) return
    mark('modal:mount')
    markClickToModal('modal:mounted (component created, image tag about to render)')
    document.addEventListener('keydown', this.handleKeydown)
    document.body.classList.add('work-detail-open')

    this.$nextTick(() => {
      this.$refs.pageContent?.focus({ preventScroll: true })
    })

    this._mountedSlug = this.$route.params.workSlug
    this._isDirectHit = !this.navContext

    // Check if router marked this as "not found" via meta
    if (this.$route.meta.workNotFound) {
      this.showNotAvailable = true
      this.requestedSlug = this.$route.meta.requestedSlug || null
      this.scheduleRedirect()
    } else if (this.isArchivedWithoutContext) {
      this.scheduleRedirect()
    }
  },
  beforeUnmount() {
    if (this.preview) return
    // Clear timeout if component is destroyed
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout)
    }
    // Stop slideshow if playing
    this.stopSlideshow()
    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleKeydown)
    document.body.classList.remove('work-detail-open')
    // Clear navigation context (will be set again if opening from gallery block)
    mutations.clearNavigationContext()
  },
  methods: {
    scheduleRedirect() {
      this.redirectTimeout = setTimeout(() => {
        this.$router.push(this.pageSlug ? '/' + this.pageSlug : { name: 'Home' })
      }, 3000)
    },
    toggleInfoPanel() {
      if (this.showForm) {
        // Close form first, then close panel
        this.showForm = false
        this.isPanelOpen = false
      } else {
        this.isPanelOpen = !this.isPanelOpen
        // Opening the panel = user wants to read; pause the slideshow so
        // it doesn't yank the content out from under them.
        if (this.isPanelOpen && this.isSlideshowPlaying) {
          this.stopSlideshow()
        }
      }
    },
    formatPrice(value) {
      const num = parseFloat(value)
      return isNaN(num) ? value : num.toLocaleString()
    },
    // Build route object for navigating to a work, preserving page context
    workRoute(workSlug) {
      return {
        name: 'work',
        params: { workSlug, ...(this.pageSlug && { pageSlug: this.pageSlug }) }
      }
    },
    onCancel() {
      const currentSlug = this.$route.params.workSlug
      const navigated = currentSlug !== this._mountedSlug
      if (this._isDirectHit || navigated) {
        mutations.setLastClosedWork(currentSlug)
      }
      if (this.sourcePath) {
        this.$router.push(this.sourcePath)
      } else if (this.pageSlug) {
        this.$router.push('/' + this.pageSlug)
      } else {
        this.$router.push({ name: 'Home' })
      }
    },
    onImageLoad() {
      if (this.preview) {
        this.imageLoaded = true
        return
      }
      mark('modal:image:load')
      summary('direct-url boot')
      markClickToModal('image:loaded (browser fired <img> load event)')
      endClickToModal()
      this.imageLoaded = true
      this.lockedImageHeight = null
      this.preloadAdjacent()
    },
    // Direct GCS full image failed: retry with the wrapper URL once, then treat as loaded.
    onMainImageError() {
      if (!this.imageDirectFailed && this.mainImageSrc !== this.work?.imageFile) {
        this.imageDirectFailed = true
        return
      }
      this.onImageLoad()
    },
    preloadAdjacent() {
      // Warm the browser cache for the neighbours so prev/next nav is instant.
      if (!this.navigationWorks?.length) return
      const urls = []
      const next = this.navigationWorks[this.currentIndex + 1]
      const prev = this.navigationWorks[this.currentIndex - 1]
      const fullUrl = w => (w?.imageRef ? directGcsUrl(w.imageRef) : wrapperToDirectUrl(w?.imageFile)) || w?.imageFile
      if (next) urls.push(fullUrl(next))
      if (prev) urls.push(fullUrl(prev))
      urls.filter(Boolean).forEach(src => { const img = new Image(); img.src = src })
    },
    handleKeydown(event) {
      // Tab trapping: keep focus inside the modal
      if (event.key === 'Tab') {
        const modal = this.$refs.pageContent
        if (!modal) return
        const focusable = modal.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), a[href]'
        )
        if (!focusable.length) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
        return
      }

      // Don't handle shortcuts if user is typing in a form field
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT') {
        return
      }

      // Don't intercept browser shortcuts (Ctrl+R, Ctrl+Shift+R, Ctrl+W, etc.)
      if (event.ctrlKey || event.metaKey || event.altKey) return

      switch (event.key) {
        case 'Escape':
          if (this.isImmersive) {
            this.exitImmersive()
          } else {
            this.onCancel()
          }
          break
        case 'ArrowLeft':
          if (this.hasPrevious) {
            this.stopSlideshow()
            this.goToPrevious()
          }
          break
        case 'ArrowRight':
          if (this.hasNext) {
            this.stopSlideshow()
            this.goToNext()
          }
          break
        case ' ':
          event.preventDefault()
          this.toggleSlideshow()
          break
        case 'r':
        case 'R':
          this.goToRandom()
          break
      }
    },
    onImageClick(e) {
      if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return
      this.enterImmersive()
    },
    onTouchStart(e) {
      if (e.touches.length >= 2) {
        this.isMultiTouch = true
        const t1 = e.touches[0], t2 = e.touches[1]
        this.pinchStartDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
        this.pinchMidX = (t1.clientX + t2.clientX) / 2
        this.pinchMidY = (t1.clientY + t2.clientY) / 2
        return
      }
      this.isMultiTouch = false
      this.touchStartX = e.touches[0].clientX
      this.touchStartY = e.touches[0].clientY
    },
    onTouchEnd(e) {
      this.immersivePanning = false
      if (this.isMultiTouch) {
        this.isMultiTouch = false
        this.pinchStartDist = null
        // If one finger remains, track it for potential pan
        if (e.touches.length === 1) {
          this.touchStartX = e.touches[0].clientX
          this.touchStartY = e.touches[0].clientY
        } else {
          this.touchStartX = null
          this.touchStartY = null
        }
        return
      }
      if (this.touchStartX === null) return
      if (this.isImmersive && window.visualViewport && window.visualViewport.scale > 1.05) {
        this.touchStartX = null
        this.touchStartY = null
        return
      }
      const touch = e.changedTouches[0]
      const dx = touch.clientX - this.touchStartX
      const dy = touch.clientY - this.touchStartY
      this.touchStartX = null
      this.touchStartY = null
      if (this.isImmersive) {
        const isTap = Math.abs(dx) < 15 && Math.abs(dy) < 15
        const now = Date.now()
        const isDoubleTap = isTap && (now - this.lastTapTime) < 300

        if (isDoubleTap) {
          this.lastTapTime = 0
          if (this.immersiveScale > 1) {
            this.resetImmersiveZoom()
          } else {
            this.immersiveScale = 2
            this.immersiveTranslateX = -touch.clientX
            this.immersiveTranslateY = -touch.clientY
            this.clampImmersivePan()
          }
          return
        }
        if (isTap) { this.lastTapTime = now; return }

        // When zoomed, block swipe navigation so user can pan freely
        if (this.immersiveScale > 1) return

        if (dy > 80 && Math.abs(dy) > Math.abs(dx)) { this.exitImmersive(); return }
        if (Math.abs(dy) >= 50 && Math.abs(dy) > Math.abs(dx)) return
        if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx > 0 && this.hasPrevious) this.goToPrevious()
          else if (dx < 0 && this.hasNext) this.goToNext()
        }
      } else {
        if (this.isSlideshowPlaying) this.stopSlideshow()
        if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx > 0 && this.hasPrevious) this.goToPrevious()
          else if (dx < 0 && this.hasNext) this.goToNext()
          return
        }
        if (Math.abs(dy) >= 50 && Math.abs(dy) > Math.abs(dx)) return
        if (!touch.target.closest('button')) this.enterImmersive()
      }
    },
    onImmersivePan(e) {
      if (!this.isImmersive) return
      e.preventDefault()

      if (e.touches.length === 2 && this.pinchStartDist !== null) {
        const t1 = e.touches[0], t2 = e.touches[1]
        const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
        const midX = (t1.clientX + t2.clientX) / 2
        const midY = (t1.clientY + t2.clientY) / 2
        const newScale = Math.max(1, Math.min(5, this.immersiveScale * (dist / this.pinchStartDist)))
        const ratio = newScale / this.immersiveScale
        this.immersiveTranslateX = midX + ratio * (this.immersiveTranslateX - this.pinchMidX)
        this.immersiveTranslateY = midY + ratio * (this.immersiveTranslateY - this.pinchMidY)
        this.immersiveScale = newScale
        this.pinchStartDist = dist
        this.pinchMidX = midX
        this.pinchMidY = midY
        if (newScale <= 1) { this.resetImmersiveZoom() } else { this.clampImmersivePan() }
        return
      }

      if (e.touches.length === 1 && this.immersiveScale > 1) {
        this.immersivePanning = true
        const touch = e.touches[0]
        this.immersiveTranslateX += touch.clientX - this.touchStartX
        this.immersiveTranslateY += touch.clientY - this.touchStartY
        this.touchStartX = touch.clientX
        this.touchStartY = touch.clientY
        this.clampImmersivePan()
      }
    },
    clampImmersivePan() {
      const overlay = this.$refs.immersiveOverlay
      if (!overlay) return
      const W = overlay.offsetWidth
      const H = overlay.offsetHeight
      const s = this.immersiveScale
      this.immersiveTranslateX = Math.max(W * (1 - s), Math.min(0, this.immersiveTranslateX))
      this.immersiveTranslateY = Math.max(H * (1 - s), Math.min(0, this.immersiveTranslateY))
    },
    enterImmersive() {
      this.isImmersive = true
      this.flashImmersiveTitle()
    },
    resetImmersiveZoom() {
      this.immersiveScale = 1
      this.immersiveTranslateX = 0
      this.immersiveTranslateY = 0
      this.immersivePanning = false
      this.pinchStartDist = null
    },
    exitImmersive() {
      this.isImmersive = false
      this.resetImmersiveZoom()
      this.lastTapTime = 0
      this.showImmersiveTitle = false
      clearTimeout(this.immersiveTitleTimeout)
    },
    flashImmersiveTitle() {
      clearTimeout(this.immersiveTitleTimeout)
      this.showImmersiveTitle = true
      this.immersiveTitleTimeout = setTimeout(() => {
        this.showImmersiveTitle = false
      }, 3000)
    },
    goToPrevious() {
      if (!this.hasPrevious) return
      this.stopSlideshow()
      this.showForm = false
      this.resetImmersiveZoom()
      if (this.isImmersive) this.flashImmersiveTitle()
      const prevWork = this.navigationWorks[this.currentIndex - 1]
      this.lockedImageHeight = this.$refs.viewerImage?.offsetHeight + 'px'
      this.imageLoaded = false
      this.$router.push(this.workRoute(prevWork.slug || prevWork.id))
    },
    goToNext() {
      if (!this.hasNext) return
      this.stopSlideshow()
      this.showForm = false
      this.resetImmersiveZoom()
      if (this.isImmersive) this.flashImmersiveTitle()
      const nextWork = this.navigationWorks[this.currentIndex + 1]
      this.lockedImageHeight = this.$refs.viewerImage?.offsetHeight + 'px'
      this.imageLoaded = false
      this.$router.push(this.workRoute(nextWork.slug || nextWork.id))
    },
    // Slideshow methods
    toggleSlideshow() {
      if (this.isSlideshowPlaying) {
        this.stopSlideshow()
      } else {
        this.startSlideshow()
      }
    },
    startSlideshow() {
      if (!this.navigationWorks || this.navigationWorks.length <= 1) return

      this.isSlideshowPlaying = true
      this.slideshowInterval = setInterval(() => {
        // Navigate to next work without stopping slideshow
        let nextWork
        if (this.hasNext) {
          nextWork = this.navigationWorks[this.currentIndex + 1]
        } else {
          // Loop back to the first work
          nextWork = this.navigationWorks[0]
        }
        this.imageLoaded = false
        this.$router.push(this.workRoute(nextWork.slug || nextWork.id))
      }, this.slideshowDelay)
    },
    stopSlideshow() {
      this.isSlideshowPlaying = false
      if (this.slideshowInterval) {
        clearInterval(this.slideshowInterval)
        this.slideshowInterval = null
      }
    },
    // Random work navigation
    goToRandom() {
      if (!this.navigationWorks || this.navigationWorks.length <= 1) return
      this.showForm = false
      if (this.isSlideshowPlaying) {
        this.stopSlideshow()
      }

      // Get a random work that's not the current one
      let randomIndex
      do {
        randomIndex = Math.floor(Math.random() * this.navigationWorks.length)
      } while (randomIndex === this.currentIndex && this.navigationWorks.length > 1)

      const randomWork = this.navigationWorks[randomIndex]
      this.imageLoaded = false
      this.$router.push(this.workRoute(randomWork.slug || randomWork.id))
    }
  }
}
</script>

<style scoped>
/* Layout follows the modal's own width, so an admin preview always shows the desktop page */
.work-detail-page {
  --panel-w: clamp(280px, 32cqi, 420px);
  container: work-modal / inline-size;
  position: fixed;
  inset: 0;
  overflow: hidden;
  z-index: 101;
  display: flex;
  flex-direction: column;
  background-color: var(--color-bg);

  &.is-preview {
    position: absolute;
    z-index: 1;
  }
}

.work-detail-content {
  --ribbon-h: 56px;

  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;

  @container work-modal (width < 768px) {
    overflow: auto;
  }
}

/* ── Ribbon ── */
.modal-ribbon {
  flex: 0 0 var(--ribbon-h);
  display: flex;
  align-items: center;
  background-color: var(--panel-bg, var(--color-bg));
  color: var(--color-text);
  padding: 6px 0;
  margin-bottom: -1px;
  z-index: 1;
  overflow: visible;

  @container work-modal (width >= 768px) {
    padding-top: 12px;
    padding-bottom: 6px;
  }

  @container work-modal (width < 768px) {
    position: relative;
    padding: 6px 0;
    pointer-events: auto;
  }
}

.ribbon-text {
  flex: 1 1 auto;
  overflow: hidden;
  padding: 0 8px 0 15px;
  text-align: left;
}

.ribbon-line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--panel-text, var(--color-text));
}

.ribbon-title {
  font-family: var(--font-decorative);
  font-size: var(--font-sm);
}

.ribbon-controls {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
  padding-right: 18px;

  @container work-modal (width < 768px) {
    gap: 16px;
    padding-right: 12px;
  }

  /* Mobile: center controls */
  @container work-modal (width < 768px) {
    display: flex;
    justify-content: center !important;
    gap: 40px !important;
    width: auto !important;
    margin: 0;
    padding: 0 8px 0 0;
    box-sizing: border-box;
  }
}

/* On desktop, focus ribbon controls align over the side panel below. */
/* Width matches --panel-w so buttons spread across the panel width when */
/* their natural size is smaller; space-between distributes them evenly. */
.layout-focus .ribbon-controls {
  @container work-modal (width >= 768px) {
    min-width: var(--panel-w);
    width: max-content;
    box-sizing: border-box;
    justify-content: space-between;
    gap: 12px;
    padding: 0 18px;
  }
}

.ribbon-controls .modal-control-button {
  @container work-modal (width >= 768px) {
    flex-shrink: 0; /* never squish buttons */
  }
}

.modal-control-button.ribbon-close {
  position: static;
  transform: none;

  @container work-modal (width >= 768px) {
    margin-left: 10px;
  }

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
}

/* ── Always-open: hide ribbon, controls live inside the info panel ── */
.layout-always-open .modal-ribbon {
  @container work-modal (width >= 768px) {
    display: none;
  }
}

.side-panel :deep(.buyForm) {
  padding-top: 2rem;
  overflow: visible;
}

.panel-controls {
  display: none; /* hidden on mobile, ribbon handles controls there */
  justify-content: space-evenly;
  padding: 1.2em 0 1.5em;
  margin: 0 -1.5em; /* counteract parent padding to span full width */

  @container work-modal (width >= 768px) {
    display: flex;
  }
}

/* ── Body: image + info panel ── */
.modal-body {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 0;
}

/* Shared image container */
.viewer-image {
  position: relative;
  display: flex;
  background-color: var(--color-bg);
  overflow: hidden;
  padding: 0 50px;
  flex: 1 1 auto;
  min-height: 0;

  @container work-modal (max-width: 1024px) {
    padding: 0;
  }

  &.loading {
    @container work-modal (width < 768px) {
      min-height: 300px;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--color-header-text) 12%, transparent) 50%, transparent 100%);
      animation: shimmer 2.2s ease-in-out infinite;
      z-index: 1;
    }

    img {
      opacity: 0;
    }
  }

  &.loaded img {
    animation: fadeIn 0.25s ease-out forwards;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    margin: 0;
    opacity: 0;
    cursor: pointer;
  }
}

/* Always-open: image takes 70% on desktop */
.layout-always-open .viewer-image {
  @container work-modal (width >= 768px) {
    flex: 1 1 70%; /* flex-shrink: 1 so it yields space to the panel's min-width */
  }
}

/* ── Info panel ── */
.side-panel {
  flex: 0 0 auto;
  width: 0;
  overflow: hidden;
  transition: width 0.35s ease;
  background-color: var(--panel-bg, var(--color-bg));
  color: var(--color-text);
  border-left: 1px solid color-mix(in srgb, var(--color-text) 15%, transparent);
  box-shadow: inset 6px 0 8px -6px rgba(0, 0, 0, 0.35);

  &.panel-is-open {
    width: var(--panel-w);
    overflow-y: auto;
  }

  /* The preview is static; the panel takes the wheel back so a long panel still scrolls */
  .is-preview & {
    pointer-events: auto;

    & > * {
      pointer-events: none;
    }
  }
}

/* Always-open: panel always visible at 30%, min 280px */
.layout-always-open .side-panel {
  @container work-modal (width >= 768px) {
    width: 30%;
    min-width: 280px;
    overflow-y: auto;
    transition: none;
  }
}

.title-nav {
  display: contents;
}

.title-nav-btn,
.title-nav-spacer {
  display: none !important;
}

/* The contact form replaces the info panel; on mobile the title row stays (below) */
.info-panel-body.form-open {
  display: none;
}

.info-panel-body {
  min-width: var(--panel-w);
  padding: 3em 1.5em 1.5em;
  font-size: var(--font-sm);
  font-family: var(--font-primary);
  color: inherit;
  line-height: 1.4;
  text-align: center;
  display: flex;
  flex-direction: column;

  h1 {
    font-size: 1.8em;
    margin-bottom: 0.4em;
    line-height: 1.2;
    font-family: var(--font-decorative);
    font-weight: normal;
  }

  h2,
  .work-description,
  li {
    font-size: 1em;
    font-weight: normal;
  }

  h2 {
    margin: 0.1em 0 0;
    line-height: 1.3;
    font-family: var(--font-primary);
    font-size: var(--font-sm);
  }

  .gallery-caption {
    margin: 0.1em 0 0.3em;
    font-size: var(--font-sm);
    font-family: var(--font-primary);
    letter-spacing: 0.03em;
    line-height: 1.4;

  }


  .work-description {
    margin-top: 0;
    margin-bottom: 1em;
    padding: 0.5em;
    text-align: left;

    &.short-description {
      text-align: center;
    }

    & :deep(.rm-btn) {
      color: var(--panel-text, var(--color-text));

      &:hover {
        color: color-mix(in srgb, var(--panel-text, var(--color-text)) 80%, transparent);
      }
    }
  }

  .details-spacer {
    display: none;
  }

  .work-details {
    margin: 0 0 1.5em;
    padding-left: 0;
    list-style: none;
  }

  li {
    margin: 0.4em 0;
  }

  .work-tags-section {
    margin-top: 1.5em;
    text-align: center;
  }

  .work-tags-heading {
    display: block;
    margin-bottom: 0.6em;
    font-size: 0.7em;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: color-mix(in srgb, var(--panel-text, var(--color-text)) 80%, var(--panel-bg, var(--color-bg)));
  }

  .work-tags {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.4em;
  }

  .work-tag-group {
    display: contents;
  }

  .work-tag-chip {
    display: inline-block;
    padding: 2px 10px;
    border: 1px solid color-mix(in srgb, var(--panel-text, var(--color-text)) 25%, var(--panel-bg, var(--color-bg)));
    border-radius: 999px;
    font-size: 0.85em;
  }

  #cta {
    margin-top: 1.5em;
  }
}

/* Always-open: panel body fills the 30% naturally */
.layout-always-open .side-panel {
  @container work-modal (width >= 768px) {
    display: flex;
    flex-direction: column;
  }
}

.layout-always-open .info-panel-body {
  @container work-modal (width >= 768px) {
    min-width: 0;
    width: 100%;
    padding-top: 0;
    flex: 1;
  }
}

.layout-always-open .info-panel-body h1 {
  @container work-modal (width >= 768px) {
    margin-top: auto;
  }
}

.layout-always-open .info-panel-body>*:last-child {
  @container work-modal (width >= 768px) {
    margin-bottom: auto;
  }
}

/* ── Toggle pill (focus mode only) ── */
.panel-toggle-pill {
  height: 30px;
  padding: 0 14px;
  border: 1.5px solid var(--panel-text, var(--color-text));
  border-radius: 20px;
  background-color: transparent;
  color: var(--panel-text, var(--color-text));
  font-family: var(--font-primary);
  font-size: var(--font-xs);
  font-weight: 500;
  cursor: pointer;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  transition: background-color 0.2s, opacity 0.2s;
  white-space: nowrap;
  opacity: 0.75;

  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
    opacity: 1;
  }

  &.open {
    background-color: rgba(255, 255, 255, 0.15);
    opacity: 1;
  }
}

/* ── Control buttons ── */
.work-detail-content :focus-visible {
  outline: 2px solid var(--color-highlight);
  outline-offset: 2px;
}

.modal-control-button {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, transform 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  @media (hover: hover) {
    &:hover {
      background-color: var(--color-white);
      transform: scale(1.1);
    }
  }

  &:active {
    transform: scale(0.95);
  }

  @container work-modal (width < 768px) {
    width: 34px;
    height: 34px;
  }
}

.modal-slideshow-button {
  span {
    font-size: 18px;
    line-height: 1;
    color: var(--color-dark-gray);
  }

  @container work-modal (width < 768px) {
    span {
      font-size: var(--font-xs);
    }
  }
}

.modal-random-button {
  span {
    font-size: 20px;
    line-height: 1;
    color: var(--color-dark-gray);
  }

  @container work-modal (width < 768px) {
    span {
      font-size: var(--font-sm);
    }
  }
}

/* ── Navigation buttons ── */
.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
}

.modal-control-button.nav-button {
  &:hover {
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  @container work-modal (max-width: 1024px) {
    background-color: rgba(255, 255, 255, 0.7);
  }
}

.nav-prev {
  left: 5px;
}

.nav-next {
  right: 5px;
}

/* ── Misc ── */
.btnGroup {
  margin: 5px;
  min-width: 200px;
}

.preview-placeholder {
  opacity: 0.6;
  font-style: italic;
}

/* ── Mobile: title-nav replaces image nav arrows (stacked layout only) ── */
@container work-modal (width < 768px) {
  .nav-button {
    display: none;
  }

  .title-nav {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;

    h1 {
      flex: 1;
      min-width: 0;
      margin-bottom: 0;
      overflow-wrap: break-word;
      word-break: break-word;
    }

    .work-title {
      font-size: 1.3em;
    }
  }

  .title-nav-btn {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex !important;
  }

  .title-nav-spacer {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: block !important;
  }
}

/* ── Mobile: both modes collapse to stacked layout ── */
/* The .work-detail-content itself is overflow: auto, so the whole page scrolls naturally on mobile. */
@container work-modal (width < 768px) {
  .modal-body {
    flex-direction: column;
    overflow: visible;
  }

  .viewer-image {
    flex: 0 0 auto;
    height: 55vh;
    touch-action: pan-y pinch-zoom;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  /* Panel always visible on mobile (no toggle) */
  .side-panel,
  .side-panel.panel-is-open {
    width: 100% !important;
    flex: 1 0 auto;
    overflow: visible;
    transition: none;
    display: flex;
    flex-direction: column;
  }


  .info-panel-body {
    min-width: 0;
    width: 100%;
    padding: 1.5em;
    text-align: center;
    display: flex;
    flex-direction: column;

    .details-spacer {
      display: block;
      height: 1em;
    }

    .work-details {
      margin: 0;
    }

    #cta {
      margin-top: 1.5em;
    }
  }

  /* Form open: keep only the title row, so the title and arrows don't move */
  .info-panel-body.form-open {
    display: flex;
    padding-bottom: 0;
  }

  .info-panel-body.form-open > :not(.title-nav) {
    display: none;
  }

  .panel-toggle-pill {
    display: none;
  }

  :deep(.buyForm) {
    flex: 1 0 100% !important;
    width: 100% !important;
    padding: 1.5em !important;
  }
}

/* ── Loading / Not-available states ── */
.modal-loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  width: 100%;
  background-color: var(--color-lightest-gray);

  @container work-modal (width >= 768px) {
    min-height: 90vh;
  }
}

.not-available-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
  width: 100%;
  background-color: var(--color-white);
}

.not-available-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--color-red);
  color: var(--color-white);
  font-size: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  animation: scaleIn 0.3s ease-out;

  &.archived-icon {
    background-color: var(--color-red);
    font-size: 40px;
  }
}

.not-available-title {
  font-size: 24px;
  font-weight: bold;
  margin: 10px 0;
  color: var(--color-dark-gray);
}

.not-available-message {
  font-size: var(--font-sm);
  color: var(--color-muted);
  margin: 10px 0 30px 0;
}

.not-available-redirect {
  font-size: var(--font-sm);
  color: var(--color-muted);
  font-style: italic;
  animation: pulse 1.5s ease-in-out infinite;
}

/* ── Immersive view ── */
.immersive-fade-enter-active {
  transition: opacity 0.25s ease;
}

.immersive-fade-leave-active {
  transition: opacity 0.2s ease;
}

.immersive-fade-enter-from,
.immersive-fade-leave-to {
  opacity: 0;
}

.immersive-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-bg);
}

.immersive-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  touch-action: none;
}

.immersive-title {
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: var(--font-decorative);
  font-size: 1.1em;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8), 0 0 12px rgba(0, 0, 0, 0.6);
  padding: 8px 24px;
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;

  &.visible {
    opacity: 1;
  }
}

.immersive-back {
  position: absolute;
  top: 12px;
  right: 12px;

  @container work-modal (width >= 768px) {
    top: calc(1.2em + 1px);
    right: calc(var(--panel-w) / 4 - 8px);
  }
}

.immersive-nav {
  display: none;

  @media (hover: hover) and (pointer: fine) {
    display: flex;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.6;
    transition: opacity 0.2s, transform 0.2s;

    &:hover {
      opacity: 1;
    }

    &.immersive-prev {
      left: 16px;
    }

    &.immersive-next {
      right: 16px;
    }

    &:hover {
      &.immersive-prev {
        transform: translateY(-50%) scale(1.1);
      }

      &.immersive-next {
        transform: translateY(-50%) scale(1.1);
      }
    }
  }
}

/* ── Keyframes ── */
@keyframes shimmer {
  0% {
    left: -100%;
  }

  100% {
    left: 100%;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.75;
  }
}
</style>
